import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const timeUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  if (diff <= 0) return null;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (hrs < 24) return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

const dueUrgency = (iso) => {
  if (!iso) return 'normal';
  const diff = new Date(iso) - new Date();
  if (diff < 0) return 'overdue';
  if (diff < 24 * 60 * 60 * 1000) return 'urgent';
  if (diff < 3 * 24 * 60 * 60 * 1000) return 'soon';
  return 'normal';
};

const formatIST = (utcDateString) => {
  const date = new Date(utcDateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

// ── Skeleton + Reusable components ──────────────────────────────────────────

const SkeletonRow = () => (
  <div className="flex items-center justify-between p-6">
    <div className="space-y-2 flex-1"><div className="skeleton h-5 w-48" /><div className="skeleton h-4 w-32" /></div>
    <div className="skeleton h-9 w-20 rounded-xl" />
  </div>
);

const SkeletonCardGrid = ({ count = 3 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="skeleton h-5 w-24 mb-3 rounded-full" />
        <div className="skeleton h-5 w-40 mb-2" />
        <div className="skeleton h-4 w-32 mb-4" />
        <div className="skeleton h-10 w-full rounded-xl" />
      </div>
    ))}
  </div>
);

const StudentSkeleton = () => (
  <Layout>
    <div className="space-y-8">
      <section>
        <div className="skeleton h-6 w-32 mb-4" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="skeleton h-8 w-16 mb-2" /><div className="skeleton h-4 w-24" />
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="skeleton h-6 w-40 mb-4" />
        <SkeletonCardGrid count={3} />
      </section>
      <section>
        <div className="skeleton h-6 w-28 mb-4" />
        <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
          <SkeletonRow /><SkeletonRow /><SkeletonRow />
        </div>
      </section>
    </div>
  </Layout>
);

const EmptyState = ({ icon, message, hint }) => (
  <div className="flex flex-col items-center justify-center py-14 text-gray-400">
    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">{icon}</div>
    <p className="text-sm font-medium text-gray-500">{message}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const SectionHeader = ({ icon, title, children }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">{icon}</div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    </div>
    {children}
  </div>
);

// ── Icons ───────────────────────────────────────────────────────────────────

const Icons = {
  calendar: <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  play:     <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  clipboard:<svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
  chart:    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  emptyCalendar: <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  emptyPlay:<svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  emptyClipboard: <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>,
};

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const styles = { success: 'bg-emerald-50 border-emerald-200 text-emerald-800', error: 'bg-red-50 border-red-200 text-red-800' };
  const icons  = { success: 'M5 13l4 4L19 7', error: 'M6 18L18 6M6 6l12 12' };
  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Submit Assignment Modal ─────────────────────────────────────────────────

const SubmitModal = ({ assignment, onSubmit, onCancel, loading }) => {
  const [submissionText, setSubmissionText] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!submissionText.trim() && !attachmentUrl.trim()) {
      return setFormError('Please provide either a text answer or a link.');
    }
    onSubmit({
      submission_text: submissionText.trim() || null,
      attachment_url: attachmentUrl.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Submit Assignment</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>}
          <div className="p-4 bg-gray-50 rounded-xl space-y-1">
            <h4 className="font-medium text-gray-900">{assignment.title}</h4>
            {assignment.description && <p className="text-sm text-gray-500">{assignment.description}</p>}
            <p className="text-xs text-gray-400">Due: {formatIST(assignment.due_date)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Answer</label>
            <textarea value={submissionText} onChange={(e) => { setSubmissionText(e.target.value); setFormError(''); }} rows={5}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Type your answer here..." autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Attachment Link <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" value={attachmentUrl} onChange={(e) => { setAttachmentUrl(e.target.value); setFormError(''); }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="https://drive.google.com/... or GitHub link" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onCancel} disabled={loading} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const StudentDashboard = () => {
  const { user } = useAuth();
  const [enrollments, setEnrollments]     = useState([]);
  const [classes, setClasses]             = useState([]);
  const [recordings, setRecordings]       = useState([]);
  const [assignments, setAssignments]     = useState([]);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [submitting, setSubmitting]       = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toast, setToast]                 = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [classesRes, recordingsRes, assignmentsRes, attendanceRes, batchesRes] = await Promise.all([
          api.get('/api/classes'),
          api.get('/api/recordings'),
          api.get('/api/assignments'),
          api.get(`/api/attendance/students/${user.id}`),
          api.get('/api/batches'),
        ]);

        setEnrollments(batchesRes.data || []);

        const now = new Date();
        const upcoming = (classesRes.data || [])
          .filter((c) => c.status === 'scheduled' && new Date(c.scheduled_at) > new Date(now - 60 * 60 * 1000))
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
          .slice(0, 5);
        setClasses(upcoming);

        setRecordings((recordingsRes.data || []).slice(0, 6));

        setAssignments(
          (assignmentsRes.data || []).sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        );

        const records = attendanceRes.data || [];
        const total   = records.length;
        const present = records.filter((r) => r.status === 'present' || r.status === 'late').length;
        setAttendanceStats({ total, present, absent: total - present, rate: total > 0 ? Math.round((present / total) * 100) : 0 });
      } catch {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user.id]);

  const handleAssignmentSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      await api.post(`/api/assignments/${submitting.id}/submit`, data);
      setSubmitting(null);
      setToast({ message: 'Assignment submitted successfully!', type: 'success' });
      // Update status in-place instead of removing
      setAssignments((prev) =>
        prev.map((a) => a.id === submitting.id ? { ...a, my_submission_status: 'submitted' } : a)
      );
    } catch (err) {
      setToast({ message: getErrorMessage(err, 'Failed to submit assignment'), type: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) return <StudentSkeleton />;

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">Retry</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-8 animate-fade-in">

        {/* ── Enrollment Info ──────────────────────────────────────────── */}
        {enrollments.length > 0 && (
          <section>
            <SectionHeader
              icon={<svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>}
              title="My Enrollment"
            />
            <div className={`grid gap-4 ${enrollments.length === 1 ? 'grid-cols-1 max-w-xl' : 'grid-cols-1 md:grid-cols-2'}`}>
              {enrollments.map((batch) => {
                const now   = new Date();
                const start = new Date(batch.start_date);
                const end   = new Date(batch.end_date);
                const status = now < start ? 'upcoming' : now > end ? 'completed' : 'active';
                const statusStyles = {
                  active:    { bg: 'rgba(32,201,151,0.12)', color: '#059669', label: 'Active' },
                  upcoming:  { bg: 'rgba(245,158,11,0.12)',  color: '#d97706', label: 'Upcoming' },
                  completed: { bg: 'rgba(107,114,128,0.12)', color: '#6b7280', label: 'Completed' },
                };
                const s = statusStyles[status];
                const trainers = batch.trainers?.map(t => t.full_name).join(', ') || '—';

                return (
                  <div
                    key={batch.id}
                    className="relative overflow-hidden rounded-2xl shadow-sm border border-white/10 p-6 flex flex-col gap-3"
                    style={{ background: 'linear-gradient(135deg, #003479 0%, #00256e 100%)' }}
                  >
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: '#20C997', transform: 'translate(30%, -30%)' }} />
                    <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full opacity-5" style={{ background: '#fff', transform: 'translate(-30%, 30%)' }} />

                    {/* Status badge */}
                    <div className="flex items-center justify-between relative">
                      <span className="text-xs font-bold uppercase tracking-wider text-white/50">Enrolled Course</span>
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                    </div>

                    {/* Course + batch */}
                    <div className="relative">
                      <h3 className="text-lg font-bold text-white leading-snug">{batch.course_title || 'Course'}</h3>
                      <p className="text-sm font-medium mt-0.5" style={{ color: '#20C997' }}>{batch.batch_name}</p>
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 relative">
                      <div className="flex items-center gap-1.5 text-xs text-white/60">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(batch.start_date)} — {formatDate(batch.end_date)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-white/60">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {trainers}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Attendance Stats */}
        {attendanceStats && (
          <section>
            <SectionHeader icon={Icons.chart} title="My Attendance" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { value: `${attendanceStats.rate}%`, label: 'Attendance Rate', color: 'text-primary' },
                { value: attendanceStats.total,   label: 'Total Classes',  color: 'text-gray-900' },
                { value: attendanceStats.present, label: 'Attended',       color: 'text-emerald-600' },
                { value: attendanceStats.absent,  label: 'Missed',         color: 'text-red-500' },
              ].map((stat, i) => (
                <div key={stat.label}
                  className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200"
                  style={{ animationDelay: `${i * 80}ms` }}>
                  <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming Classes */}
        <section>
          <SectionHeader icon={Icons.calendar} title="Upcoming Classes" />
          {classes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState icon={Icons.emptyCalendar} message="No upcoming classes scheduled" hint="Classes will appear here once your trainer schedules them" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {classes.map((cls) => {
                const remaining = timeUntil(cls.scheduled_at);
                return (
                  <div key={cls.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 p-6 flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">{cls.batch_name || 'Batch'}</span>
                      {remaining && (
                        <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {remaining}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{cls.title}</h3>
                    <p className="text-sm text-gray-500 mb-1">{formatIST(cls.scheduled_at)}</p>
                    <p className="text-sm text-gray-400 mb-4">{cls.trainer_name || 'Trainer'} &middot; {cls.duration_minutes || 60} min</p>
                    <div className="mt-auto">
                      {cls.meeting_link ? (
                        <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer"
                          className="block w-full text-center py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors text-sm">
                          Join Class
                        </a>
                      ) : (
                        <button disabled className="w-full py-2.5 bg-gray-100 text-gray-400 font-medium rounded-xl text-sm cursor-not-allowed">
                          {remaining ? `Starts in ${remaining}` : 'Scheduled'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Assignments */}
        <section>
          <SectionHeader icon={Icons.clipboard} title="Assignments" />
          {assignments.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState icon={Icons.emptyClipboard} message="No assignments right now" hint="When your trainer assigns work, it will show up here" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
              {assignments.map((a) => {
                const urgency = dueUrgency(a.due_date);
                const urgencyStyles = {
                  overdue: 'text-red-700 bg-red-50 border-red-200',
                  urgent: 'text-red-600 bg-red-50 border-red-200',
                  soon: 'text-amber-600 bg-amber-50 border-amber-200',
                  normal: 'text-gray-600 bg-gray-50 border-gray-200',
                };
                const urgencyLabel = {
                  overdue: 'Overdue',
                  urgent: 'Due today',
                  soon: 'Due soon',
                  normal: formatIST(a.due_date),
                };
                return (
                  <div key={a.id} className="flex items-center justify-between p-6 hover:bg-gray-50/50 transition-colors">
                    <div className="flex-1 min-w-0 mr-4">
                      <h3 className="font-semibold text-gray-900 truncate">{a.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{a.class_title || 'Class'} &middot; {a.batch_name || 'Batch'}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${urgencyStyles[urgency]}`}>{urgencyLabel[urgency]}</span>
                      {a.my_submission_status === 'graded' ? (
                        <span className="px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-xl border border-emerald-200">
                          Grade: {a.my_submission_grade || '—'}
                        </span>
                      ) : a.my_submission_status === 'submitted' ? (
                        <span className="px-4 py-2 bg-amber-50 text-amber-700 text-sm font-medium rounded-xl border border-amber-200">
                          Pending Review
                        </span>
                      ) : (
                        <button onClick={() => setSubmitting(a)}
                          className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors">Submit</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Recent Recordings */}
        <section>
          <SectionHeader icon={Icons.play} title="Recent Recordings" />
          {recordings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState icon={Icons.emptyPlay} message="No recordings available yet" hint="Recordings will be added after each class by your trainer" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recordings.map((rec) => (
                <div key={rec.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 overflow-hidden flex flex-col">
                  {/* Thumbnail */}
                  <div className="h-36 bg-gradient-to-br from-secondary/90 to-secondary flex items-center justify-center relative">
                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{rec.title || rec.class_title || 'Recording'}</h3>
                    <p className="text-sm text-gray-500 mb-3">{rec.batch_name || 'Batch'}{rec.uploader_name ? ` · ${rec.uploader_name}` : ''}</p>
                    <div className="mt-auto">
                      <a href={rec.youtube_link} target="_blank" rel="noopener noreferrer"
                        className="block w-full text-center py-2.5 bg-secondary text-white font-medium rounded-xl hover:bg-secondary/90 transition-colors text-sm">
                        Watch Recording
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {submitting && (
        <SubmitModal assignment={submitting} onSubmit={handleAssignmentSubmit} onCancel={() => setSubmitting(null)} loading={submitLoading} />
      )}
    </Layout>
  );
};

export default StudentDashboard;
